import DatePicker from 'react-datepicker';
import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import { useCompany } from '../../../hooks/use-company';
import { useAuth } from '../../../hooks/use-auth';
import StyledError from '../../../components/common/form/styled-error';
import { strings } from '../../../localization/strings';
import CommonButtonDefault from '../../../components/common/buttons/default';
import * as companiesService from '../../../services/api/companies';
import { getUnixTimestamp, isValidSubscriptionDates } from '../../../services/formatter/date';
import AlertPrimary from '../../../components/common/alerts/alert-primary';
import StyledSwitch from '../../../components/common/form/styled-switch';
import Tooltip from '../../../components/common/tooltip';

type InputsEltaSense = {
  elta_sense_active: number;
  elta_sense_from: Date;
  elta_sense_to: Date;
};

function ServicesEltaSenseForm() {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState<boolean>(false);
  const { token } = useAuth();
  const { updateCompany, companyDetails } = useCompany();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<InputsEltaSense>({
    defaultValues: {
      elta_sense_active: companyDetails.elta_sense_active,
      elta_sense_from: companyDetails.elta_sense_from ? new Date(companyDetails.elta_sense_from * 1000) : undefined,
      elta_sense_to: companyDetails.elta_sense_to ? new Date(companyDetails.elta_sense_to * 1000) : undefined,
    },
  });

  const onSubmitEltaSense: SubmitHandler<InputsEltaSense> = async (data) => {
    try {
      if (companyDetails?.id) {
        setIsLoading(true);

        const formData = {
          ...data,
          elta_sense_active: data.elta_sense_active ? 1 : 0,
          elta_sense_from:
            data.elta_sense_from && data.elta_sense_active ? getUnixTimestamp(data.elta_sense_from) : null,
          elta_sense_to: data.elta_sense_to && data.elta_sense_active ? getUnixTimestamp(data.elta_sense_to) : null,
        };

        const res = await companiesService.update(token, companyDetails?.id, formData);

        if (res) {
          setIsLoading(false);
          setIsSubmitSuccessful(true);

          updateCompany(formData);

          setTimeout(() => {
            setIsSubmitSuccessful(false);
          }, 2000);
        }
      }
    } catch (e) {
      setIsLoading(false);
      if (e instanceof Error) {
        setError(e.message);
      }
    }
  };

  return (
    <>
      {error && (
        <div className="max-w-7xl mx-auto my-5 mt-8">
          <AlertPrimary type="danger" text={error} />
        </div>
      )}
      {isSubmitSuccessful && (
        <div className="mb-2 mt-8">
          <AlertPrimary size="xs" type="success" text="Sėkmingai atnaujinta" />
        </div>
      )}
      <form onSubmit={handleSubmit(onSubmitEltaSense)}>
        <div className="bg-white sm:rounded-lg overflow-hidden ring-1 ring-gray-900/5 mt-8">
          <div className="p-4 font-medium leading-6 bg-gray-50 text-gray-900 flex flex-row">
            <h3>{strings.companies.services.eltaSense}</h3>
            <div className="ml-auto">
              <CommonButtonDefault type="submit" isLoading={isLoading} xs primary>
                {strings.button.save}
              </CommonButtonDefault>
            </div>
            <div className="ml-4">
              <Tooltip
                disabled={!isValidSubscriptionDates(watch('elta_sense_from'), watch('elta_sense_to'))}
                title={
                  <StyledSwitch
                    name="elta_sense_active"
                    control={control}
                    disabled={isValidSubscriptionDates(watch('elta_sense_from'), watch('elta_sense_to'))}
                  />
                }
              >
                {strings.companies.services.disableServiceTooltip}
              </Tooltip>
            </div>
          </div>

          {watch('elta_sense_active') === 1 && (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-x-4 p-4">
              <div className="col-span-1">
                <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                  Data nuo
                </label>
                <div className="mt-1">
                  <Controller
                    control={control}
                    name="elta_sense_from"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DatePicker
                        showYearDropdown
                        dateFormat="yyyy-MM-dd"
                        isClearable
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        selected={field.value ? new Date(field.value) : undefined}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                        disabledKeyboardNavigation
                      />
                    )}
                  />
                  {errors.elta_sense_from && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                </div>
              </div>
              <div className="col-span-1">
                <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                  Data iki
                </label>
                <div className="mt-1">
                  <Controller
                    control={control}
                    name="elta_sense_to"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DatePicker
                        showYearDropdown
                        dateFormat="yyyy-MM-dd"
                        isClearable
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        selected={field.value ? new Date(field.value) : undefined}
                        onChange={(e) => {
                          field.onChange(e);
                        }}
                        disabledKeyboardNavigation
                      />
                    )}
                  />
                  {errors.elta_sense_to && <StyledError>{strings.error.fieldIsRequired}</StyledError>}
                </div>
              </div>
            </div>
          )}
        </div>
      </form>
    </>
  );
}

export default ServicesEltaSenseForm;
